import {
  WaitlistActiveTicketType,
  WaitlistEmptyReason,
  WaitlistEventStatusAccount,
  WaitlistSyncMeta,
} from '../types';

/**
 * Which of the three "no rows" situations we're in. `data: []` alone is
 * ambiguous, and telling a customer "you're not on any waitlists" when we've
 * simply never synced is the one way this screen actively misleads.
 */
export const waitlistEmptyReason = (sync: WaitlistSyncMeta): WaitlistEmptyReason => {
  if (sync.account_count === 0) return 'no_accounts';
  if (sync.last_attempted_at === null) return 'never_synced';
  return 'genuinely_none';
};

/**
 * "Mar 5", or "Mar 5, 2025" once the year stops being obvious. Sized for a
 * pill, where the badge text is already most of the width.
 */
export const formatJoinedShort = (joinedAt: string | null): string | null => {
  if (!joinedAt) return null;
  const dt = new Date(joinedAt);
  if (Number.isNaN(dt.getTime())) return null;
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

export interface WaitlistBadge {
  /** Short pill text. Must stay a few words — it sits in a narrow grid cell. */
  label: string;
  /** Optional wrapped line under the row, where the long explanation goes. */
  detail: string | null;
  tone: 'warn' | 'info' | 'neutral';
}

/**
 * Does this account already hold tickets for this event?
 *
 * Ownership is answered by active_tickets alone, never by the waitlist status:
 * it counts tickets bought outside the waitlist too, and on real data three
 * accounts held tickets while their entry still read timed-out.
 */
export const holdsTickets = (account: WaitlistEventStatusAccount | undefined): boolean =>
  (account?.active_tickets ?? 0) > 0;

/**
 * What an account holds, e.g. `2 × VIP, 2 × Early Bird`.
 *
 * Groups arrive biggest first and their counts sum to the total, so the list is
 * rendered in order and in full — a group with no name is a real group of
 * tickets stored without a type, and dropping it would leave the tiers not
 * adding up to the count sitting right next to them.
 *
 * `selectedTicketTypeName` is the tier the picker is about to join. When a
 * group's name matches it, that half of the label is repeating the header
 * above and only the release tier is kept — `Second Tier` rather than
 * `Saturday - General Admission (Saturday - Second Tier)`. The comparison is
 * whole-field: the API gives `name` and `tier_name` separately, so nothing here
 * splits a string it was handed.
 *
 * One group covering every ticket needs no arithmetic spelled out: the badge
 * beside it already says how many, so it renders as the bare tier name.
 */
export const formatActiveTicketTypes = (
  groups: WaitlistActiveTicketType[],
  activeTickets: number,
  selectedTicketTypeName?: string | null,
): string | null => {
  if (!groups || groups.length === 0) return null;

  const sameAsSelected = (name: string | null) =>
    !!name &&
    !!selectedTicketTypeName &&
    name.trim().toLowerCase() === selectedTicketTypeName.trim().toLowerCase();

  const named = (group: WaitlistActiveTicketType) => {
    // Joining the very tier they already hold: the name is the header's, so
    // the release is the only thing left worth printing — and if there is no
    // release either, the count above has already said everything.
    if (sameAsSelected(group.name)) return group.tier_name ?? null;
    const name = group.name ?? 'type not recorded';
    return group.tier_name ? `${name} (${group.tier_name})` : name;
  };

  if (groups.length === 1) {
    const only = named(groups[0]);
    if (!only) return null;
    return groups[0].count === activeTickets ? only : `${groups[0].count} × ${only}`;
  }

  return groups
    .map((group) => {
      const label = named(group);
      return label ? `${group.count} × ${label}` : `${group.count} × the tier you're joining`;
    })
    .join(', ');
};

/**
 * Badges for one account row on the join picker, waitlist state first.
 *
 * Waitlist state leads because it is what the picker is for: joining an
 * account that is already queued replaces its entry. Ownership is real but
 * secondary, and hidden until asked for — see `includeTickets`.
 *
 * The two are independent facts and each gets its own badge: an account can
 * hold tickets AND sit on the list for another tier, and collapsing that into
 * one pill loses whichever half came second.
 *
 * This flags, it does not block. Joining again is a legitimate choice — the
 * customer may want a different tier, or a second pair — so every account
 * stays selectable.
 *
 * Worth knowing while reading the copy: Dice REPLACES an existing entry when
 * you join a second ticket type for the same event, silently and with no
 * error, so the cost of joining over an entry is losing the tier named on its
 * second line.
 */
export interface WaitlistBadgeOptions {
  /** The tier about to be joined, so the badges can stop repeating it. */
  selectedTicketTypeName?: string | null;
  /** Include what the account already holds. Off by default: ownership is
   *  background detail, and showing it on every row buries the one fact the
   *  picker exists to convey — whether this account is on the list already. */
  includeTickets?: boolean;
}

export const waitlistBadgesFor = (
  account: WaitlistEventStatusAccount | undefined,
  { selectedTicketTypeName, includeTickets = false }: WaitlistBadgeOptions = {},
): WaitlistBadge[] => {
  if (!account) return [];

  const badges: WaitlistBadge[] = [];
  const entry = account.waitlist;

  // A timed-out entry gets no badge at all: the spot is gone, so it is not a
  // fact about this account worth a row of its own. Whatever it did leave
  // behind shows up as tickets, below.
  if (entry && entry.status !== 'timed-out') {
    // The status set is open, so anything unrecognised falls through to the
    // "on this list" copy rather than to nothing.
    //
    // joined_at is set only for joins made THROUGH FlashTools, and is null for
    // the ~95% joined in the Dice app. So the date appears exactly when we are
    // the ones who put the account on the list, and stays off otherwise rather
    // than standing in first_seen_at — which is when our sync first saw the
    // row, ie. today for a months-old entry.
    const joinedOn = formatJoinedShort(entry.joined_at);
    const sameTier =
      !!entry.ticket_type_name &&
      !!selectedTicketTypeName &&
      entry.ticket_type_name.trim().toLowerCase() === selectedTicketTypeName.trim().toLowerCase();
    badges.push({
      label: joinedOn ? `Already on this waitlist · joined ${joinedOn}` : 'Already on this waitlist',
      // Naming the tier they are queued for only helps when it differs from
      // the one being joined — that difference is the whole reason to hesitate.
      detail: sameTier ? null : entry.ticket_type_name,
      tone: 'warn',
    });
  }

  if (includeTickets && account.active_tickets > 0) {
    const n = account.active_tickets;
    // The picker is scoped to one event, so the event goes unnamed here — the
    // header above already names it, and repeating it on every row is noise.
    badges.push({
      label: `Already have ${n} ticket${n === 1 ? '' : 's'}`,
      detail: formatActiveTicketTypes(account.active_ticket_types, n, selectedTicketTypeName),
      tone: 'info',
    });
  }

  return badges;
};

export interface SmartJoinPlan {
  /** Accounts to join, with the amount each should ask for. Never 0. */
  quantities: Map<number, number>;
  /** Skipped: joining again would replace the entry they already hold. */
  onWaitlist: number[];
  /** Skipped: the event's limit leaves them no room for another ticket. */
  atLimit: number[];
}

/**
 * Works out what a smart join should actually send.
 *
 * Two accounts never make it into the payload: one already on the list, whose
 * entry Dice would replace rather than add to, and one with no room left under
 * the event's ticket cap.
 *
 * Room is read from the best source available, in order:
 *
 *  1. `tickets_remaining` on the account — the only field that answers the
 *     question directly. Not returned yet; used the moment it is.
 *  2. `limit - held`, where limit is the event's `max_tickets`. Documented as
 *     the per-ORDER cap, and used here as the per-event one because in
 *     practice it is the number Dice refuses against. An account holding 3 of
 *     4 is joinable for 1, and that top-up is worth having.
 *  3. Neither known (`max_tickets` is null on plenty of events): an account
 *     holding ANY tickets is treated as having no room. This is the case that
 *     sent five accounts a request for 2 apiece and had every one refused —
 *     with no cap to subtract from, a full request is a guess.
 */
export const planSmartJoin = ({
  targeted,
  accounts,
  limit,
  wanted,
  snap,
}: {
  targeted: number[];
  accounts: Map<number, WaitlistEventStatusAccount>;
  /** The event's max_tickets. Null when the event does not declare one. */
  limit: number | null;
  /** What the customer asked for, per account. */
  wanted: number;
  /** Snaps an amount down to one the tier actually sells. */
  snap: (amount: number) => number;
}): SmartJoinPlan => {
  const quantities = new Map<number, number>();
  const onWaitlist: number[] = [];
  const atLimit: number[] = [];

  for (const id of targeted) {
    const account = accounts.get(id);
    const entry = account?.waitlist;
    if (entry && entry.status !== 'timed-out') {
      onWaitlist.push(id);
      continue;
    }

    const held = account?.active_tickets ?? 0;
    const room =
      account?.tickets_remaining ??
      (limit != null ? Math.max(limit - held, 0) : held > 0 ? 0 : wanted);
    const amount = snap(Math.min(wanted, room));

    // `snap` floors to an amount the tier sells, but a tier whose smallest
    // increment already exceeds the room left has nothing to offer here.
    if (room <= 0 || amount <= 0 || amount > room) {
      atLimit.push(id);
      continue;
    }

    quantities.set(id, amount);
  }

  return { quantities, onWaitlist, atLimit };
};

/**
 * Where an account sorts in the picker: 0 free to join, 1 already holds
 * tickets, 2 already on the list.
 *
 * The order is "least reason to hesitate first" — the rows a customer can
 * check without reading anything sit at the top, and the two that carry a
 * consequence sink below them.
 *
 * A timed-out entry does not count as being on the list: the spot is gone, so
 * such an account ranks on its ticket count alone, same as one that never
 * joined.
 */
export const waitlistPickerRank = (account: WaitlistEventStatusAccount | undefined): number => {
  if (!account) return 0;
  if (account.waitlist && account.waitlist.status !== 'timed-out') return 2;
  if (account.active_tickets > 0) return 1;
  return 0;
};

export const waitlistAccountsById = (
  accounts: WaitlistEventStatusAccount[],
): Map<number, WaitlistEventStatusAccount> => {
  const map = new Map<number, WaitlistEventStatusAccount>();
  for (const account of accounts) {
    map.set(account.account_id, account);
  }
  return map;
};

/** "synced 4m ago" — driven by last_complete_at, the freshness floor. */
export const formatSyncedAgo = (isoStr: string | null): string => {
  if (!isoStr) return 'never fully synced';
  const then = new Date(isoStr).getTime();
  if (Number.isNaN(then)) return 'never fully synced';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'synced just now';
  if (mins < 60) return `synced ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `synced ${hours}h ago`;
  return `synced ${Math.floor(hours / 24)}d ago`;
};

/**
 * Event start rendered in the venue's timezone. The API gives a real UTC
 * instant plus an IANA zone, so unlike the sold-out feed we can convert
 * properly instead of reading wall-clock components off the string.
 */
export const formatEventStart = (utc: string, timezone: string | null): string => {
  const dt = new Date(utc);
  if (Number.isNaN(dt.getTime())) return utc;
  try {
    return dt.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone || undefined,
    });
  } catch {
    // Bad/unknown IANA zone — fall back to the viewer's locale rather than throw.
    return dt.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
};

/** joined_at is null for ~95% of entries. Show nothing rather than guessing. */
export const formatJoinedAt = (joinedAt: string | null): string | null => {
  if (!joinedAt) return null;
  const dt = new Date(joinedAt);
  if (Number.isNaN(dt.getTime())) return null;
  return `Joined ${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};
